import type { ToolDefinition, ToolHandlers } from './tools/llmToolsTypes';

export type { ToolDefinition, ToolHandlers };

export type ConversationSummary = {
  id: string;
  externalId?: string;
  agent: string;
  createdAt: string;
  title?: string;
  metadata?: {
    relatedQuestions?: string[];
    customLegalDisclaimer?: boolean;
  };
};

export interface ConversationPageResponse {
  content: ConversationSummary[];
  last: boolean;
  number: number;
  size: number;
}

export interface CreateConversationResponse {
  id: string;
  agent: string;
}

export type ChartType = 'pie' | 'bar' | 'column' | 'line' | 'area' | 'sidewaysBar';
export type ChartDataRow = Record<string, string | number>;
export type ChartRequest = {
  chartType: ChartType;
  title?: string;
  description?: string;
  categoryField: string;
  valueFields: string[];
  data: ChartDataRow[];
};

export type ConversationItemContent = {
  input_type?: string;
  text?: string;
  callId?: string;
  name?: string;
  arguments?: string | Record<string, unknown>;
  output?: string | Record<string, unknown>;
  status?: string;
  charts?: ChartRequest[];
  // The fields of the gateway's structured answer other than the text itself.
  metadata?: {
    relatedQuestions?: string[];
    legalDisclaimer?: boolean;
    title?: string;
  };
};

export interface ConversationItem {
  // The gateway sends no id or content for items it can't map, such as reasoning, and no
  // content for a message whose first part isn't text.
  id?: string;
  role: string;
  type?: string;
  content?: ConversationItemContent;
  createdAt?: string;
  [key: string]: unknown;
}

export interface ConversationItemsResponse {
  items: ConversationItem[];
  nextPage?: string | null;
}

export type ConversationData = {
  conversationId: string;
  agent: string;
  title?: string;
  messages: ConversationItem[];
  streamingMessage: string;
  isLoading: boolean;
  reasoningMessage: string | null;
  error: string | null;
  nextPageCursor?: string | null;
  hasMoreItems: boolean;
  isLoadingMore: boolean;
};

export type ChatUIState = 'closed' | 'open' | 'minimized';
export type ChatOpenConversationMode = 'existing' | 'new';

export type AiChatHiddenContext = {
  text: string;
  data?: Record<string, unknown>;
};

export type ChatOpenPayload = {
  /** User-visible message text persisted in the conversation thread. */
  message?: string;
  /**
   * Model-only context posted as a hidden SYSTEM item before `message` is sent.
   * Filtered from the chat UI on load via `filterSystemMessages`.
   */
  hiddenContext?: AiChatHiddenContext;
  metadata?: Record<string, unknown>;
  autoSend?: boolean;
  conversationMode?: ChatOpenConversationMode;
  /** When set, controls whether the LLM request enables web search. Omitted for manual chat sends. */
  enableWebSearch?: boolean;
};

export interface AiChatState {
  chatUIState: ChatUIState;
  chatOpenPayload: ChatOpenPayload | null;
  minimizedPreview: string | null;
  currentConversationId: string | null;

  conversationsData: Record<string, ConversationData>;

  context: unknown;
  platformSpecificTools: ToolDefinition[];
  platformSpecificToolsHandlers: ToolHandlers;

  conversationsList: ConversationSummary[];
  isLoadingConversations: boolean;
  isFetchingInitialConversations: boolean;
  conversationsMeta: {
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
  /**
   * Epoch counter used to discard stale conversation-list responses.
   *
   * Bumped whenever the list is invalidated (e.g. bulk delete resets pagination).
   * The list thunks capture the value when they start and echo it back in their
   * fulfilled/rejected payloads; the reducers ignore any result whose value no
   * longer matches, so a request that was in flight during a clear can't write
   * deleted conversations or outdated pagination back into the store.
   */
  conversationsRequestGeneration: number;

  error: string | null;
}

export const AI_CHAT_REDUCER_KEY = 'aiChatStore';

export enum ChatAgents {
  SIMPLIFI_RESPONSES_CHAT = 'simplifi-responses-chat',
  QBP_RESPONSES_CHAT = 'qbp-responses-chat',
  AI_INSIGHTS = 'ai-insights',
  AI_INSIGHTS_V2 = 'ai-insights-v2',
}
export type ChatAgentValue = ChatAgents | string;
export const DEFAULT_AGENT = ChatAgents.SIMPLIFI_RESPONSES_CHAT;

export const mkConversationData = (conversationId: string, agent: string): ConversationData => ({
  conversationId,
  agent,
  messages: [],
  streamingMessage: '',
  isLoading: false,
  reasoningMessage: null,
  error: null,
  nextPageCursor: null,
  hasMoreItems: false,
  isLoadingMore: false,
});

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [AI_CHAT_REDUCER_KEY]: AiChatState;
  }
}
